/* eslint-disable max-len */
import { Request } from '@/composables/GraphQL';
import { SpmTableState } from '@/types';

interface ListResponse<T> {
  items: Array<T>;
  total: number;
  err: string;
}

export default async function getTemplatesUsedBySyncElement({ idShop, syncElementIds }: { idShop: number; syncElementIds: string[] }): Promise<SpmTableState> {
  const query = `query ( $id_shop: Int!, $sync_element_ids: [String!]! ) {
    TemplatesListUsedBySyncElement(id_shop: $id_shop, sync_element_ids: $sync_element_ids)
      {
        items {
          id_template
          label
          type
          name_marketing_workflow
          date_modification
          template_config
        }
        total
      }
    }`;

  const variables = { id_shop: idShop, sync_element_ids: syncElementIds };

  const {
    data,
    err,
  } = await Request<ListResponse<any>>({
    name: 'TemplatesListUsedBySyncElement',
    query,
    variables,
  });

  const result: ListResponse<any> = data ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items.map((item) => ({ ...item, label: item.name_marketing_workflow ? `${item.label} (${item.name_marketing_workflow})` : item.label })),
    total: result.total,
  };
}

export async function getTemplatesNotUsedBySyncElement({
  idShop,
  syncElementId,
  templateType,
  elementPartTypeOrSubtype,
  limit = 10,
  lastTemplateId,
  isPartType,
  excludeAutomationTemplates = false,
  excludeIds = [],
}: {
  idShop: number;
  syncElementId: string;
  templateType: string;
  elementPartTypeOrSubtype: string;
  limit?: number;
  lastTemplateId?: number;
  isPartType?: boolean;
  excludeAutomationTemplates?: boolean;
  excludeIds?: number[];
}): Promise<SpmTableState> {
  const query = `query ( $id_shop: Int!, $sync_element_id: String!, $template_type: String!, $element_part_type_or_subtype: String!, $limit: Int, $last_template_id: Int, $is_part_type: Boolean, $exclude_automation_templates: Boolean, $exclude_ids: [Int] ) {
    TemplatesListNotUsedBySyncElement(id_shop: $id_shop, sync_element_id: $sync_element_id, template_type: $template_type, element_part_type_or_subtype: $element_part_type_or_subtype, limit: $limit, last_template_id: $last_template_id, is_part_type: $is_part_type, exclude_automation_templates: $exclude_automation_templates, exclude_ids: $exclude_ids)
      {
        items {
          id_template
          label
          type
          name_marketing_workflow
          imageKey
          template_config
          date_modification
        }
        total
      }
    }`;

  const variables = {
    id_shop: idShop,
    sync_element_id: syncElementId,
    template_type: templateType,
    element_part_type_or_subtype: elementPartTypeOrSubtype,
    limit,
    last_template_id: lastTemplateId,
    is_part_type: isPartType,
    exclude_automation_templates: excludeAutomationTemplates,
    exclude_ids: excludeIds,
  };

  const {
    data,
    err,
  } = await Request<ListResponse<any>>({
    name: 'TemplatesListNotUsedBySyncElement',
    query,
    variables,
  });

  const result: ListResponse<any> = data ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items,
    total: result.total,
  };
}

export async function getMarketingWorkflowsWithTemplatesNotUsedBySyncElement({
  idShop,
  syncElementId,
  templateType,
  elementPartTypeOrSubtype,
  limit = 10,
  lastWorkflowId,
  lastCreationDate,
  isPartType,
  excludeIds = [],
}: {
  idShop: number;
  syncElementId: string;
  templateType: string;
  elementPartTypeOrSubtype: string;
  limit?: number;
  lastWorkflowId?: number;
  lastCreationDate?: string;
  isPartType?: boolean;
  excludeIds?: number[];
}): Promise<SpmTableState> {
  const query = `query ( $id_shop: Int!, $sync_element_id: String!, $template_type: String!, $element_part_type_or_subtype: String!, $limit: Int, $last_workflow_id: Int, $last_creation_date: String, $is_part_type: Boolean, $exclude_ids: [Int] ) {
    MarketingWorkflowsWithTemplatesNotUsedBySyncElement(id_shop: $id_shop, sync_element_id: $sync_element_id, template_type: $template_type, element_part_type_or_subtype: $element_part_type_or_subtype, limit: $limit, last_workflow_id: $last_workflow_id, last_creation_date: $last_creation_date, is_part_type: $is_part_type, exclude_ids: $exclude_ids)
      {
        items {
          id_marketing_workflow
          name_marketing_workflow
          date
          templates {
            id_template
            label
            type
            imageKey
            template_config
            date_modification
          }
        }
        total
      }
    }`;

  const variables = {
    id_shop: idShop,
    sync_element_id: syncElementId,
    template_type: templateType,
    element_part_type_or_subtype: elementPartTypeOrSubtype,
    limit,
    last_workflow_id: lastWorkflowId,
    last_creation_date: lastCreationDate,
    is_part_type: isPartType,
    exclude_ids: excludeIds,
  };

  const {
    data,
    err,
  } = await Request<ListResponse<any>>({
    name: 'MarketingWorkflowsWithTemplatesNotUsedBySyncElement',
    query,
    variables,
  });

  const result: ListResponse<any> = data ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items,
    total: result.total,
  };
}

export async function getCountTemplatesUsedBySyncElement({ idShop, syncElementIds }: { idShop: number; syncElementIds: string[] }): Promise<SpmTableState> {
  const query = `query ( $id_shop: Int!, $sync_element_ids: [String!]! ) {
    TemplatesCountBySyncElementIds(id_shop: $id_shop, sync_element_ids: $sync_element_ids)
      {
        items {
          sync_element_id
          template_count
        }
        total
      }
    }`;

  const variables = { id_shop: idShop, sync_element_ids: syncElementIds };

  const {
    data,
    err,
  } = await Request<ListResponse<any>>({
    name: 'TemplatesCountBySyncElementIds',
    query,
    variables,
  });

  const result: ListResponse<any> = data ?? {
    items: [],
    total: 0,
    err: '',
  };

  return {
    error: result.err,
    items: result.items,
    total: result.total,
  };
}
